import React, { useState, useEffect, useContext } from "react";
import clsx from "clsx";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  Switch,
  TextField,
  Button,
  Box,
  IconButton,
  Collapse,
  Select,
  MenuItem,
  FormControl,
} from "@material-ui/core";
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Settings as TestIcon,
} from "@material-ui/icons";
import api from "../../services/api";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { Section } from "../ui";
import { usePanelStyles, SettingRow } from "../FormFields/panel";
import { getTokens } from "../../styles/tokens";
import { filterOutboundConnections } from "../../utils/channels";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    root: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr)",
      gap: 16,
      paddingBottom: 8,
    },
    // cartão recolhido: sem a sobra abaixo do título
    sectionClosed: { "& > div:first-child": { marginBottom: 0 } },
    expandButton: {
      width: 32,
      height: 32,
      padding: 0,
      color: t.textSecondary,
      transition: theme.transitions.create("transform", {
        duration: theme.transitions.duration.shortest
      })
    },
    expandButtonOpen: {
      transform: "rotate(180deg)"
    },
    // chave liga/desliga: no celular o texto e a chave continuam lado a lado
    toggleRow: {
      "&:first-child": { paddingTop: 0 },
      [theme.breakpoints.down("xs")]: {
        flexDirection: "row",
        alignItems: "center",
        gap: 14,
        "& > div:last-child": { justifyContent: "flex-end" },
      },
    },
    wideRow: { "&:first-child": { paddingTop: 0 } },
    // campo de mensagem abaixo da lista de opções da seção
    messageBlock: { paddingTop: 16, borderTop: `1px solid ${t.lineRow}` },
    messagePreview: {
      marginTop: 10,
      padding: "10px 12px",
      backgroundColor: t.muted,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
      "& .MuiTypography-body2": { marginTop: 2, overflowWrap: "anywhere" },
    },
    variableChip: {
      backgroundColor: t.tint.soft,
      color: t.brand,
      padding: "2px 8px",
      borderRadius: t.radius.badge,
      fontSize: "0.75rem",
      fontFamily: "monospace",
      margin: "0 2px"
    },
    testButton: { marginTop: 10 },
    statusDot: { marginRight: 8 },
    notice: {
      marginTop: 14,
      "& strong": { display: "block", marginBottom: 4, color: t.text, fontWeight: 600 },
      "& code": {
        padding: "1px 6px",
        borderRadius: t.radius.badge,
        backgroundColor: t.paper,
        border: `1px solid ${t.line}`,
        fontSize: 12,
      },
      "& li + li": { marginTop: 4 },
      "& ul": { margin: 0, paddingLeft: 18 },
    },
    footer: { display: "flex", justifyContent: "flex-end" },
  };
});

const BirthdaySettings = () => {
  const classes = useStyles();
  const p = usePanelStyles();
  const { whatsApps } = useContext(WhatsAppsContext);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    user: true,
    contact: true,
    general: true
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await api.get("/birthdays/settings");
      setSettings(data.data);
    } catch (error) {
      toast.error("Erro ao carregar configurações de aniversário");
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/birthdays/settings", settings);
      toast.success("Configurações de aniversário salvas com sucesso! 🎉");
    } catch (error) {
      toast.error("Erro ao salvar configurações");
    } finally {
      setSaving(false);
    }
  };

  const handleTestMessage = async (messageType) => {
    // Para teste, vamos pedir um contato válido
    const contactId = prompt("Digite o ID de um contato para teste:");
    if (!contactId) return;

    try {
      await api.post("/birthdays/test-message", {
        contactId: parseInt(contactId),
        messageType
      });
      toast.success("Mensagem de teste enviada! 📱");
    } catch (error) {
      toast.error("Erro ao enviar mensagem de teste");
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderMessagePreview = (message, type) => {
    if (!message) return null;

    let previewMessage = message;
    previewMessage = previewMessage.replace(
      /{nome}/g, 
      `<span class="${classes.variableChip}">João Silva</span>`
    );
    previewMessage = previewMessage.replace(
      /{idade}/g, 
      `<span class="${classes.variableChip}">30</span>`
    );

    return (
      <Box className={classes.messagePreview}>
        <Typography variant="caption" color="textSecondary">
          Pré-visualização:
        </Typography>
        <Typography 
          variant="body2" 
          dangerouslySetInnerHTML={{ __html: previewMessage }}
        />
      </Box>
    );
  };

  if (loading) {
    return (
      <Box className={classes.root}>
        <Typography>Carregando configurações...</Typography>
      </Box>
    );
  }

  const expandButton = (section, title) => (
    <IconButton
      className={clsx(classes.expandButton, expandedSections[section] && classes.expandButtonOpen)}
      onClick={() => toggleSection(section)}
      aria-expanded={expandedSections[section]}
      aria-label={`${expandedSections[section] ? "Recolher" : "Expandir"} ${title}`}
    >
      <ExpandMoreIcon />
    </IconButton>
  );

  return (
    <Box className={classes.root}>
      {/* Configurações de Usuários */}
      <Section
        title="Aniversários de Usuários"
        actions={expandButton("user", "Aniversários de Usuários")}
        className={clsx(!expandedSections.user && classes.sectionClosed)}
      >
        <Collapse in={expandedSections.user}>
          <div>
            <SettingRow
              className={classes.toggleRow}
              label="Habilitar notificações de aniversário de usuários"
              description="Quando habilitado, será exibido modal de parabéns e criado informativo automático"
            >
              <Switch
                checked={settings?.userBirthdayEnabled || false}
                onChange={(e) => handleSettingChange("userBirthdayEnabled", e.target.checked)}
                color="primary"
                inputProps={{ "aria-label": "Habilitar notificações de aniversário de usuários" }}
              />
            </SettingRow>

            <SettingRow
              className={classes.toggleRow}
              label="Criar informativo automático para aniversários de usuários"
              description="Cria um informativo automático que é exibido para todos os usuários da empresa"
            >
              <Switch
                checked={settings?.createAnnouncementForUsers || false}
                onChange={(e) => handleSettingChange("createAnnouncementForUsers", e.target.checked)}
                color="primary"
                disabled={!settings?.userBirthdayEnabled}
                inputProps={{ "aria-label": "Criar informativo automático para aniversários de usuários" }}
              />
            </SettingRow>
          </div>

          <div className={clsx(p.labelsAbove, classes.messageBlock)}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              multiline
              rows={3}
              label="Mensagem de aniversário para usuários"
              value={settings?.userBirthdayMessage || ""}
              onChange={(e) => handleSettingChange("userBirthdayMessage", e.target.value)}
              disabled={!settings?.userBirthdayEnabled}
              helperText="Use {nome} para incluir o nome do usuário"
            />
            {renderMessagePreview(settings?.userBirthdayMessage, "user")}
          </div>
        </Collapse>
      </Section>

      {/* Configurações de Contatos */}
      <Section
        title="Aniversários de Contatos"
        actions={expandButton("contact", "Aniversários de Contatos")}
        className={clsx(!expandedSections.contact && classes.sectionClosed)}
      >
        <Collapse in={expandedSections.contact}>
          <div>
            <SettingRow
              className={classes.toggleRow}
              label="Habilitar envio automático de mensagens de aniversário para contatos"
              description="Envia automaticamente mensagem de parabéns via WhatsApp para contatos aniversariantes"
            >
              <Switch
                checked={settings?.contactBirthdayEnabled || false}
                onChange={(e) => handleSettingChange("contactBirthdayEnabled", e.target.checked)}
                color="primary"
                inputProps={{ "aria-label": "Habilitar envio automático de mensagens de aniversário para contatos" }}
              />
            </SettingRow>
          </div>

          <div className={clsx(p.labelsAbove, classes.messageBlock)}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              multiline
              rows={4}
              label="Mensagem de aniversário para contatos"
              value={settings?.contactBirthdayMessage || ""}
              onChange={(e) => handleSettingChange("contactBirthdayMessage", e.target.value)}
              disabled={!settings?.contactBirthdayEnabled}
              helperText="Use {nome} para incluir o nome do contato e {idade} para a idade"
            />
            {renderMessagePreview(settings?.contactBirthdayMessage, "contact")}

            {settings?.contactBirthdayEnabled && (
              <Button
                variant="outlined"
                className={classes.testButton}
                startIcon={<TestIcon />}
                onClick={() => handleTestMessage("contact")}
                size="small"
              >
                Testar Mensagem
              </Button>
            )}
          </div>
        </Collapse>
      </Section>

      {/* Configurações Gerais */}
      <Section
        title="Configurações Gerais"
        actions={expandButton("general", "Configurações Gerais")}
        className={clsx(!expandedSections.general && classes.sectionClosed)}
      >
        <Collapse in={expandedSections.general}>
          <div>
            <SettingRow
              wide
              className={classes.wideRow}
              label="Horário de envio das mensagens"
              description="Horário em que as mensagens automáticas serão enviadas"
            >
              <TextField
                type="time"
                variant="outlined"
                size="small"
                fullWidth
                value={settings?.sendBirthdayTime || "09:00:00"}
                onChange={(e) => {
                  // Converter HH:MM para HH:MM:SS para compatibilidade com backend
                  const timeValue = e.target.value;
                  const formattedTime = timeValue ? `${timeValue}:00` : "09:00:00";
                  handleSettingChange("sendBirthdayTime", formattedTime);
                }}
                inputProps={{ "aria-label": "Horário de envio das mensagens" }}
              />
            </SettingRow>

            <SettingRow
              wide
              label="Conexão WhatsApp para Envio"
              description="Conexão WhatsApp que será usada para enviar mensagens de aniversário"
            >
              <FormControl fullWidth variant="outlined" size="small">
                <Select
                  displayEmpty
                  value={settings?.whatsappId || ""}
                  onChange={(e) => handleSettingChange("whatsappId", e.target.value || null)}
                  disabled={!settings?.contactBirthdayEnabled}
                  SelectDisplayProps={{ "aria-label": "Conexão WhatsApp para Envio" }}
                >
                  <MenuItem value="">
                    <em>Selecione uma conexão</em>
                  </MenuItem>
                  {whatsApps?.length > 0 &&
                    filterOutboundConnections(whatsApps).map((whatsapp) => (
                      <MenuItem key={whatsapp.id} value={whatsapp.id}>
                        <Box display="flex" alignItems="center" minWidth={0}>
                          <Box
                            component="span"
                            className={clsx(p.colorDot, classes.statusDot)}
                            bgcolor={whatsapp.status === "CONNECTED" ? "success.main" : "error.main"}
                          />
                          {whatsapp.name} ({whatsapp.status})
                        </Box>
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </SettingRow>

            <SettingRow
              className={classes.toggleRow}
              label="Criar informativo interno para aniversários de usuários"
              description="Cria um informativo interno quando um usuário faz aniversário"
            >
              <Switch
                checked={settings?.createAnnouncementForUsers || false}
                onChange={(e) => handleSettingChange("createAnnouncementForUsers", e.target.checked)}
                color="primary"
                inputProps={{ "aria-label": "Criar informativo interno para aniversários de usuários" }}
              />
            </SettingRow>
          </div>

          <div className={clsx(p.notice, classes.notice)}>
            <strong>Variáveis disponíveis nas mensagens:</strong>
            <ul>
              <li><code>{"{nome}"}</code> - Nome da pessoa</li>
              <li><code>{"{idade}"}</code> - Idade da pessoa (apenas para contatos)</li>
            </ul>
          </div>
        </Collapse>
      </Section>

      {/* Botão Salvar */}
      <div className={classes.footer}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Salvando..." : "Salvar Configurações"}
        </Button>
      </div>
    </Box>
  );
};

export default BirthdaySettings;
